import React, { useEffect, useState, lazy, Fragment } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { useSelector } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useIdleTimer } from 'react-idle-timer';

const LandingPage = lazy(() => import('../containers/LandingPage'));

const PatientHome = lazy(() => import('../containers/Patient/Home'));
const PatientAppointment = lazy(() => import('../containers/Patient/Appointment'));
const PatientSummary = lazy(() => import('../containers/Patient/Summary'));
const PatientOneOnOneSessionDetail = lazy(() =>
    import('../containers/Patient/OneOnOneSessionDetail')
);
const PatientGroupOpenSessionDetail = lazy(() =>
    import('../containers/Patient/GroupOpenSessionDetail')
);
const PatientPublic = lazy(() => import('../containers/Patient/Public'));
const PatientProfile = lazy(() => import('../containers/Patient/Profile'));
const PatientNotification = lazy(() => import('../containers/Patient/Notification'));
const PatientCall = lazy(() => import('../containers/Patient/Call'));
const PatientGroupOpenCall = lazy(() => import('../containers/Patient/GroupOpenCall'));
const PatientMessage = lazy(() => import('../containers/Patient/Message'));

const ProviderPatientDetail = lazy(() => import('../containers/Provider/PatientDetail'));
const ProviderCall = lazy(() => import('../containers/Provider/Call'));
const ProviderGroupCall = lazy(() => import('../containers/Provider/GroupCall'));
const ProviderOpenCall = lazy(() => import('../containers/Provider/OpenCall'));
const ProviderOtherOpenCall = lazy(() => import('../containers/Provider/OtherOpenCall'));
const ProviderPatient = lazy(() => import('../containers/Provider/Patient'));
const ProviderAddPatient = lazy(() => import('../containers/Provider/AddPatient'));
const ProviderProfile = lazy(() => import('../containers/Provider/Profile'));
const ProviderHospital = lazy(() => import('../containers/Provider/Hospital'));
const ProviderMessage = lazy(() => import('../containers/Provider/Message'));
const ProviderCredits = lazy(() => import('../containers/Provider/Credits'));
const ProviderInvoice = lazy(() => import('../containers/Provider/Invoice'));
const ProviderCheckout = lazy(() => import('../containers/Provider/Checkout'));
const ProviderReport = lazy(() => import('../containers/Provider/Report'));
const ProviderGroupReport = lazy(() => import('../containers/Provider/GroupReport'));
const ProviderSession = lazy(() => import('../containers/Provider/Session'));
const ProviderOneOnOneSessionDetail = lazy(() =>
    import('../containers/Provider/OneOnOneSessionDetail')
);
const ProviderGroupOpenSessionDetail = lazy(() =>
    import('../containers/Provider/GroupOpenSessionDetail')
);
const ProviderOtherOpenSessionDetail = lazy(() =>
    import('../containers/Provider/OtherOpenSessionDetail')
);
const ProviderCreateOneOnOne = lazy(() => import('../containers/Provider/CreateOneOnOne'));
const ProviderCreateGroup = lazy(() => import('../containers/Provider/CreateGroup'));
const ProviderCreateOpen = lazy(() => import('../containers/Provider/CreateOpen'));
const Calendar = lazy(() => import('../containers/Provider/Calendar'));

// import Transcribe from '../components/Transcribe';

import { Progress } from '../components/index';
import MainLayout from '../components/MainLayout';
import ReceivingCall from '../components/ReceivingCall';
import ChatWindow from '../components/Message/ChatWindow';

import { StyledMainLayoutContainer, StyledContentContainer } from '../components/MainLayout/style';

const PrivateRoute = ({ initRoute }) => {
    const history = useHistory();
    const location = useLocation();
    const user = useSelector((state) => state.user);
    const [role, setRole] = useState(null);

    useEffect(() => {
        const userRole = localStorage.getItem('role');

        if (
            (['Provider', 'Admin', 'Provider Admin'].includes(userRole) && _.isEmpty(user.staff)) ||
            (userRole === 'Patient' && _.isEmpty(user.patient))
        ) {
            localStorage.clear();

            if (userRole === 'Patient') {
                window.location.replace('/signin');
            } else {
                window.location.replace('/provider/signin');
            }
            return;
        }
        setRole(userRole);
        if (initRoute) {
            history.push(initRoute);
        }
    }, []);

    /*--------------- Idle Detection ---------------*/
    const [timeByMinute] = useState(1000 * 60 * 1);

    const matchProviderCalendar = useRouteMatch('/provider/calendar');

    const matchPatientCall = useRouteMatch('/call/:id');
    const matchPatientGroupCall = useRouteMatch('/groupcall/:id');
    const matchPatientOpenCall = useRouteMatch('/opencall/:id');
    const matchProviderCall = useRouteMatch('/provider/call/:id');
    const matchProviderGroupCall = useRouteMatch('/provider/groupcall/:id');
    const matchProviderOpenCall = useRouteMatch('/provider/opencall/:id');
    const matchProviderOtherOpenCall = useRouteMatch('/provider/other-opencall/:id');

    const onActive = () => {
        clearIdleMinutes();
    };

    const { isIdle } = useIdleTimer({ onActive, timeout: timeByMinute });

    const addIdleMinutes = (add) => {
        const minutes = localStorage.getItem('idle_minutes');

        localStorage.setItem('idle_minutes', minutes ? Number(minutes) + add : add);
    };

    const clearIdleMinutes = () => {
        localStorage.setItem('idle_minutes', 0);
    };

    useEffect(() => {
        setInterval(() => {
            const minutes = localStorage.getItem('idle_minutes');

            if (Number(minutes) >= 60) {
                localStorage.clear();

                if (role === 'Patient') {
                    window.location.replace('/signin');
                } else {
                    window.location.replace('/provider/signin');
                }
            }

            if (
                !isIdle() ||
                [
                    matchProviderCalendar,
                    matchPatientCall,
                    matchPatientGroupCall,
                    matchPatientOpenCall,
                    matchProviderCall,
                    matchProviderGroupCall,
                    matchProviderGroupCall,
                    matchProviderOpenCall,
                    matchProviderOtherOpenCall,
                ].some((item) => item)
            ) {
                return clearIdleMinutes();
            }

            addIdleMinutes(1);
        }, timeByMinute);
    }, [
        role,
        isIdle,
        matchProviderCalendar,
        matchPatientCall,
        matchPatientGroupCall,
        matchPatientOpenCall,
        matchProviderCall,
        matchProviderGroupCall,
        matchProviderGroupCall,
        matchProviderOpenCall,
        matchProviderOtherOpenCall,
    ]);

    return (
        <Fragment>
            {['app', 'dev-app', 'stg-app'].includes(window.location.host.split('.')[0]) ? (
                <Fragment>
                    {!role && (
                        <StyledMainLayoutContainer>
                            <StyledContentContainer>
                                <Switch>
                                    <Route exact path="/loading" component={Progress} />
                                    <Route path="*">
                                        <Redirect to="/loading" />
                                    </Route>
                                </Switch>
                            </StyledContentContainer>
                        </StyledMainLayoutContainer>
                    )}
                    {role === 'Patient' && (
                        <MainLayout role={role}>
                            <ReceivingCall />
                            <Switch>
                                {/* <Route exact path="/transcribe" component={Transcribe} /> */}
                                <Route exact path="/home" component={PatientHome} />
                                <Route exact path="/appointment" component={PatientAppointment} />
                                <Route
                                    exact
                                    path="/appointment/one/:id"
                                    component={PatientOneOnOneSessionDetail}
                                />
                                <Route
                                    exact
                                    path="/appointment/group/:id"
                                    component={PatientGroupOpenSessionDetail}
                                />
                                <Route
                                    exact
                                    path="/appointment/open/:id"
                                    component={PatientGroupOpenSessionDetail}
                                />
                                <Route exact path="/public" component={PatientPublic} />
                                <Route exact path="/call/:id" component={PatientCall} />
                                <Route
                                    exact
                                    path="/groupcall/:id"
                                    component={PatientGroupOpenCall}
                                />
                                <Route
                                    exact
                                    path="/opencall/:id"
                                    component={PatientGroupOpenCall}
                                />
                                <Route exact path="/summary" component={PatientSummary} />
                                <Route exact path="/summary/:id" component={PatientSummary} />
                                <Route exact path="/profile" component={PatientProfile} />
                                <Route exact path="/notification" component={PatientNotification} />
                                <Route exact path="/message" component={PatientMessage} />

                                <Route path="*">
                                    <Redirect to="/home" />
                                </Route>
                            </Switch>
                        </MainLayout>
                    )}

                    {['Provider', 'Admin', 'Provider Admin'].includes(role) && (
                        <MainLayout role={role}>
                            <Switch>
                                {/* <Route exact path="/transcribe" component={Transcribe} /> */}
                                <Route exact path="/provider/calendar" component={Calendar} />
                                <Route exact path="/provider/session" component={ProviderSession} />
                                <Route
                                    exact
                                    path="/provider/session/one/:id"
                                    component={ProviderOneOnOneSessionDetail}
                                />
                                <Route
                                    exact
                                    path="/provider/session/group/:id"
                                    component={ProviderGroupOpenSessionDetail}
                                />
                                <Route
                                    exact
                                    path="/provider/session/open/:id"
                                    component={ProviderGroupOpenSessionDetail}
                                />
                                <Route
                                    exact
                                    path="/provider/session/other-open/:id"
                                    component={ProviderOtherOpenSessionDetail}
                                />
                                <Route
                                    exact
                                    path="/provider/session/client/detail/:id"
                                    component={ProviderPatientDetail}
                                />
                                <Route
                                    exact
                                    path="/provider/session/report/:id"
                                    component={ProviderReport}
                                />
                                <Route
                                    exact
                                    path="/provider/session/group-report/:id"
                                    component={ProviderGroupReport}
                                />
                                <Route exact path="/provider/message" component={ProviderMessage} />
                                <Route
                                    exact
                                    path="/provider/calendar/one/add"
                                    component={ProviderCreateOneOnOne}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/one/edit/:id"
                                    component={ProviderCreateOneOnOne}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/one/duplicate/:id"
                                    component={ProviderCreateOneOnOne}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/group/add"
                                    component={ProviderCreateGroup}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/group/edit/:id"
                                    component={ProviderCreateGroup}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/group/duplicate/:id"
                                    component={ProviderCreateGroup}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/open/add"
                                    component={ProviderCreateOpen}
                                />
                                <Route
                                    exact
                                    path="/provider/calendar/open/edit/:id"
                                    component={ProviderCreateOpen}
                                />
                                <Route exact path="/provider/call/:id" component={ProviderCall} />
                                <Route
                                    exact
                                    path="/provider/groupcall/:id"
                                    component={ProviderGroupCall}
                                />
                                <Route
                                    exact
                                    path="/provider/opencall/:id"
                                    component={ProviderOpenCall}
                                />
                                <Route
                                    exact
                                    path="/provider/other-opencall/:id"
                                    component={ProviderOtherOpenCall}
                                />
                                <Route exact path="/provider/client" component={ProviderPatient} />
                                <Route
                                    exact
                                    path="/provider/client/add"
                                    component={ProviderAddPatient}
                                />
                                <Route
                                    exact
                                    path="/provider/client/:id"
                                    component={ProviderAddPatient}
                                />
                                <Route
                                    exact
                                    path="/provider/client/detail/:id"
                                    component={ProviderPatientDetail}
                                />
                                <Route
                                    exact
                                    path="/provider/profile/:id"
                                    component={ProviderProfile}
                                />
                                <Route
                                    exact
                                    path="/provider/consultant"
                                    component={ProviderHospital}
                                />
                                <Route
                                    exact
                                    path="/provider/consultant/add"
                                    component={ProviderProfile}
                                />
                                <Route
                                    exact
                                    path="/provider/consultant/:id"
                                    component={ProviderProfile}
                                />
                                <Route exact path="/provider/credits" component={ProviderCredits} />
                                <Route
                                    exact
                                    path="/provider/invoice/:id"
                                    component={ProviderInvoice}
                                />
                                <Route
                                    exact
                                    path="/provider/credits/checkout"
                                    component={ProviderCheckout}
                                />

                                <Route path="*">
                                    <Redirect to="/provider/session" />
                                </Route>
                            </Switch>
                            {location.pathname !== '/provider/message' && <ChatWindow />}
                        </MainLayout>
                    )}
                </Fragment>
            ) : (
                <Switch>
                    <Route exact path="/" component={LandingPage} />
                    <Route path="*">
                        <Redirect to="/" />
                    </Route>
                </Switch>
            )}
        </Fragment>
    );
};

PrivateRoute.propTypes = {
    initRoute: PropTypes.string,
};

export default PrivateRoute;
